import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Users, CalendarCheck, MapPin, CheckCircle2, UserCheck, XCircle, Loader2,
  ChevronDown, ChevronRight, Search,
} from 'lucide-react';
import clsx from 'clsx';
import { PageHeader } from '@/components/PageHeader';
import { api } from '@/lib/api';
import type { Inscricao, StatusInscricao } from '@/lib/types';

const STATUS_COR: Record<StatusInscricao, string> = {
  'Inscrito':   'bg-blue/15 text-blue-light border-blue/30',
  'Confirmado': 'bg-orange/15 text-orange border-orange/30',
  'Presente':   'bg-green/15 text-green border-green/30',
  'Cancelado':  'bg-red/15 text-red border-red/30',
  'Interesse comercial': 'bg-orange/15 text-orange border-orange/30',
};

function fmt(iso?: string) {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('pt-BR', { day: '2-digit', month: 'short', year: 'numeric' });
}

export function Inscricoes() {
  const [filtroStatus, setFiltroStatus] = useState<StatusInscricao | 'Todos'>('Todos');
  const [busca, setBusca] = useState('');
  const [aberto, setAberto] = useState<Record<string, boolean>>({});

  const eventosQ = useQuery({ queryKey: ['eventos'], queryFn: () => api.eventos.list().then((r) => r.data) });
  const inscricoesQ = useQuery({ queryKey: ['inscricoes-admin'], queryFn: () => api.inscricoes.list().then((r) => r.data) });
  const pessoasQ = useQuery({ queryKey: ['pessoas'], queryFn: () => api.pessoas.list().then((r) => r.data) });
  const solicitantesQ = useQuery({ queryKey: ['solicitantes'], queryFn: () => api.solicitantes.list().then((r) => r.data) });

  const eventos = eventosQ.data ?? [];
  const inscricoes = inscricoesQ.data ?? [];
  const pessoas = pessoasQ.data ?? [];
  const solicitantes = solicitantesQ.data ?? [];

  const pessoaMap = useMemo(() => new Map(pessoas.map((p) => [p.id, p])), [pessoas]);
  const solMap = useMemo(() => new Map(solicitantes.map((s) => [s.id, s])), [solicitantes]);

  const inscricoesFiltradas = useMemo(() => {
    const termo = busca.trim().toLowerCase();
    return inscricoes.filter((i) => {
      if (filtroStatus !== 'Todos' && i.status !== filtroStatus) return false;
      if (termo) {
        const p = i.pessoaId ? pessoaMap.get(i.pessoaId) : undefined;
        const s = i.solicitanteId ? solMap.get(i.solicitanteId) : undefined;
        const corpo = `${p?.nome ?? ''} ${p?.email ?? ''} ${s?.nome ?? ''}`.toLowerCase();
        if (!corpo.includes(termo)) return false;
      }
      return true;
    });
  }, [inscricoes, filtroStatus, busca, pessoaMap, solMap]);

  const porEvento = useMemo(() => {
    const m = new Map<string, Inscricao[]>();
    for (const i of inscricoesFiltradas) {
      const k = i.eventoId ?? '__sem_evento__';
      if (!m.has(k)) m.set(k, []);
      m.get(k)!.push(i);
    }
    return m;
  }, [inscricoesFiltradas]);

  const eventosOrdenados = useMemo(() => {
    return eventos.slice().sort((a, b) => (a.dataInicio ?? '').localeCompare(b.dataInicio ?? ''));
  }, [eventos]);

  const total = inscricoesFiltradas.length;
  const presentes = inscricoesFiltradas.filter((i) => i.status === 'Presente').length;
  const confirmados = inscricoesFiltradas.filter((i) => i.status === 'Confirmado').length;

  return (
    <div>
      <PageHeader title="Inscrições" />
      <div className="px-6 py-4 space-y-4">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <KPI label="Total" value={total} icon={<Users className="w-4 h-4" />} />
          <KPI label="Confirmados" value={confirmados} icon={<CheckCircle2 className="w-4 h-4 text-orange" />} />
          <KPI label="Presentes" value={presentes} icon={<UserCheck className="w-4 h-4 text-green" />} />
          <KPI label="Eventos" value={porEvento.size} icon={<CalendarCheck className="w-4 h-4 text-blue-light" />} />
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <div className="relative">
            <Search className="w-3.5 h-3.5 absolute left-2 top-1/2 -translate-y-1/2 text-muted" />
            <input
              type="search"
              value={busca}
              onChange={(e) => setBusca(e.target.value)}
              placeholder="Buscar por nome, email ou cliente…"
              className="input pl-7 !text-xs !py-1.5 w-72"
            />
          </div>
          <div className="flex items-center gap-1 ml-auto">
            {(['Todos', 'Inscrito', 'Confirmado', 'Presente', 'Cancelado'] as const).map((s) => (
              <button
                key={s}
                type="button"
                onClick={() => setFiltroStatus(s)}
                className={clsx(
                  'px-2.5 py-1 text-[11px] font-semibold rounded border transition-colors',
                  filtroStatus === s
                    ? 'bg-blue/20 text-blue-light border-blue/40'
                    : 'bg-panel text-text-2 border-border hover:border-border-light',
                )}
              >
                {s}
              </button>
            ))}
          </div>
        </div>

        {inscricoesQ.isLoading && <div className="card text-sm text-muted">Carregando inscrições…</div>}

        {!inscricoesQ.isLoading && porEvento.size === 0 && (
          <div className="card text-center py-12">
            <Users className="w-10 h-10 text-muted mx-auto mb-3" />
            <p className="text-sm text-muted">Nenhuma inscrição{filtroStatus !== 'Todos' ? ` em "${filtroStatus}"` : ''}.</p>
          </div>
        )}

        <div className="space-y-2">
          {eventosOrdenados.map((ev) => {
            const lista = porEvento.get(ev.id);
            if (!lista || lista.length === 0) return null;
            const isOpen = aberto[ev.id] ?? true;
            return (
              <div key={ev.id} className="bg-panel border border-border rounded-md overflow-hidden">
                <button
                  type="button"
                  onClick={() => setAberto((s) => ({ ...s, [ev.id]: !isOpen }))}
                  className="w-full flex items-center justify-between gap-3 px-3 py-2.5 hover:bg-panel-2 transition-colors text-left"
                >
                  <div className="flex items-center gap-2 min-w-0">
                    {isOpen ? <ChevronDown className="w-4 h-4 text-muted shrink-0" /> : <ChevronRight className="w-4 h-4 text-muted shrink-0" />}
                    <div className="min-w-0">
                      <div className="text-xs font-bold truncate">{ev.nome}</div>
                      <div className="text-[10px] text-muted flex items-center gap-2">
                        <span className="inline-flex items-center gap-1"><CalendarCheck className="w-3 h-3" />{fmt(ev.dataInicio)}</span>
                        {ev.local && <span className="inline-flex items-center gap-1"><MapPin className="w-3 h-3" />{ev.local}</span>}
                      </div>
                    </div>
                  </div>
                  <span className="text-[11px] font-semibold text-blue-light bg-blue/10 border border-blue/30 px-2 py-0.5 rounded">
                    {lista.length} {lista.length === 1 ? 'inscrição' : 'inscrições'}
                  </span>
                </button>
                {isOpen && (
                  <div className="border-t border-border divide-y divide-border/60">
                    {lista.map((i) => (
                      <InscricaoLinha
                        key={i.id}
                        inscricao={i}
                        pessoa={i.pessoaId ? pessoaMap.get(i.pessoaId) : undefined}
                        solicitante={i.solicitanteId ? solMap.get(i.solicitanteId) : undefined}
                      />
                    ))}
                  </div>
                )}
              </div>
            );
          })}

          {/* Inscrições sem evento associado (caso de bugs/import antigo) */}
          {(() => {
            const orphans = porEvento.get('__sem_evento__');
            if (!orphans || orphans.length === 0) return null;
            return (
              <div className="bg-panel border border-yellow/30 rounded-md p-3">
                <div className="text-xs font-bold text-yellow mb-2">
                  {orphans.length} inscrição(ões) sem evento vinculado
                </div>
                <div className="space-y-1">
                  {orphans.map((i) => (
                    <InscricaoLinha
                      key={i.id}
                      inscricao={i}
                      pessoa={i.pessoaId ? pessoaMap.get(i.pessoaId) : undefined}
                      solicitante={i.solicitanteId ? solMap.get(i.solicitanteId) : undefined}
                    />
                  ))}
                </div>
              </div>
            );
          })()}
        </div>
      </div>
    </div>
  );
}

function InscricaoLinha({ inscricao, pessoa, solicitante }: {
  inscricao: Inscricao;
  pessoa?: { nome: string; email?: string };
  solicitante?: { nome: string };
}) {
  const qc = useQueryClient();
  const mut = useMutation({
    mutationFn: (status: StatusInscricao) => api.inscricoes.update(inscricao.id, { status }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['inscricoes-admin'] }),
  });
  const pill = inscricao.status ? STATUS_COR[inscricao.status] : 'bg-muted/15 text-muted border-muted/30';
  return (
    <div className="px-3 py-2 flex items-center gap-3 hover:bg-panel-2/40">
      <div className="min-w-0 flex-1">
        <div className="text-xs font-semibold text-text truncate">{pessoa?.nome ?? '—'}</div>
        <div className="text-[10px] text-muted truncate">
          {pessoa?.email ? <span>{pessoa.email}</span> : <span>sem email</span>}
          {solicitante && <span className="text-text-2"> · {solicitante.nome}</span>}
          {inscricao.dataInscricao && <span> · {fmt(inscricao.dataInscricao)}</span>}
        </div>
      </div>
      <span className={clsx('shrink-0 inline-flex items-center px-1.5 py-0.5 rounded text-[9px] font-semibold uppercase tracking-wide border', pill)}>
        {inscricao.status ?? '—'}
      </span>
      <div className="flex items-center gap-1 shrink-0">
        <ActionBtn
          icon={<CheckCircle2 className="w-3 h-3" />}
          label="Confirmar"
          color="text-orange bg-orange/10 border-orange/30 hover:bg-orange/20"
          active={inscricao.status === 'Confirmado'}
          disabled={mut.isPending}
          onClick={() => mut.mutate('Confirmado')}
        />
        <ActionBtn
          icon={<UserCheck className="w-3 h-3" />}
          label="Presente"
          color="text-green bg-green/10 border-green/30 hover:bg-green/20"
          active={inscricao.status === 'Presente'}
          disabled={mut.isPending}
          onClick={() => mut.mutate('Presente')}
        />
        <ActionBtn
          icon={<XCircle className="w-3 h-3" />}
          label="Cancelar"
          color="text-red bg-red/10 border-red/30 hover:bg-red/20"
          active={inscricao.status === 'Cancelado'}
          disabled={mut.isPending}
          onClick={() => mut.mutate('Cancelado')}
        />
        {mut.isPending && <Loader2 className="w-3 h-3 text-muted animate-spin" />}
      </div>
    </div>
  );
}

function ActionBtn({ icon, label, color, active, disabled, onClick }: {
  icon: React.ReactNode; label: string; color: string; active: boolean; disabled: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled || active}
      title={label}
      className={clsx(
        'inline-flex items-center gap-1 px-1.5 py-1 rounded text-[10px] font-semibold border transition-colors',
        color,
        (disabled || active) && 'opacity-50 cursor-default',
      )}
    >
      {icon}
      <span className="hidden lg:inline">{label}</span>
    </button>
  );
}

function KPI({ label, value, icon }: { label: string; value: number; icon: React.ReactNode }) {
  return (
    <div className="bg-panel border border-border rounded-md p-3 flex items-center justify-between">
      <div>
        <div className="text-[10px] uppercase tracking-wider text-muted mb-0.5">{label}</div>
        <div className="text-lg font-bold text-text">{value}</div>
      </div>
      <div className="text-muted">{icon}</div>
    </div>
  );
}
